import React, { useState } from 'react';
import { X, DollarSign, Users, HardDrive, Activity, Check } from 'lucide-react';

interface PlanChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  currentPlan: string;
  onPlanChange: (tenantId: string, newPlan: string, reason: string) => Promise<void>;
}

const planDetails = {
  STARTER: {
    name: 'Starter',
    price: 29,
    limits: {
      users: 10,
      storage: 5, // GB
      apiCalls: 10000
    },
    features: [
      'Basic reporting',
      'Email support',
      'Standard integrations',
      'Mobile app access'
    ],
    color: 'bg-blue-100 text-blue-800 border-blue-200'
  },
  PROFESSIONAL: {
    name: 'Professional',
    price: 99,
    limits: {
      users: 50,
      storage: 50, // GB
      apiCalls: 100000
    },
    features: [
      'Advanced reporting',
      'Priority support',
      'Custom integrations',
      'API access',
      'Advanced analytics',
      'Custom branding'
    ],
    color: 'bg-purple-100 text-purple-800 border-purple-200'
  },
  ENTERPRISE: {
    name: 'Enterprise',
    price: 299,
    limits: {
      users: 500,
      storage: 500, // GB
      apiCalls: 1000000
    },
    features: [
      'Enterprise reporting',
      'Dedicated support',
      'Custom integrations',
      'Full API access',
      'Advanced analytics',
      'White-label solution',
      'SSO integration',
      'Audit logs',
      'Priority processing'
    ],
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
  }
};

export const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  currentPlan,
  onPlanChange
}) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedPlan === currentPlan) {
      onClose();
      return;
    }

    try {
      setLoading(true);
      await onPlanChange(tenantId, selectedPlan, reason);
      onClose();
    } catch (error) {
      console.error('Failed to change plan:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Change Plan</h2>
            <p className="text-gray-600 mt-1">Update the plan for {tenantName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Current Plan */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Current Plan</h3>
            <div className={`inline-flex items-center px-3 py-2 rounded-lg border ${planDetails[currentPlan as keyof typeof planDetails].color}`}>
              <DollarSign className="w-4 h-4 mr-2" />
              <span className="font-medium">
                {planDetails[currentPlan as keyof typeof planDetails].name} - 
                ${planDetails[currentPlan as keyof typeof planDetails].price}/month
              </span>
            </div>
          </div>

          {/* Plan Selection */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select New Plan</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {Object.entries(planDetails).map(([planKey, plan]) => (
                <div
                  key={planKey}
                  className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    selectedPlan === planKey
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${currentPlan === planKey ? 'opacity-75' : ''}`}
                  onClick={() => setSelectedPlan(planKey)}
                >
                  {selectedPlan === planKey && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                  
                  {currentPlan === planKey && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        Current
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600 ml-1">/month</span>
                    </div>
                  </div>

                  {/* Limits */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-2" />
                      <span>{plan.limits.users} users</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <HardDrive className="w-4 h-4 mr-2" />
                      <span>{plan.limits.storage}GB storage</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Activity className="w-4 h-4 mr-2" />
                      <span>{plan.limits.apiCalls.toLocaleString()} API calls</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-1">
                    {plan.features.slice(0, 4).map((feature, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <Check className="w-3 h-3 mr-2 text-green-500" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 4 && (
                      <div className="text-xs text-gray-500 mt-2">
                        +{plan.features.length - 4} more features
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Plan Comparison */}
            {selectedPlan !== currentPlan && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Plan Change Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">From:</span>
                    <div className="font-medium text-gray-900">
                      {planDetails[currentPlan as keyof typeof planDetails].name} - 
                      ${planDetails[currentPlan as keyof typeof planDetails].price}/month
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">To:</span>
                    <div className="font-medium text-gray-900">
                      {planDetails[selectedPlan as keyof typeof planDetails].name} - 
                      ${planDetails[selectedPlan as keyof typeof planDetails].price}/month
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Monthly difference:</span>
                    <span className={`font-medium ${
                      planDetails[selectedPlan as keyof typeof planDetails].price > planDetails[currentPlan as keyof typeof planDetails].price
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {planDetails[selectedPlan as keyof typeof planDetails].price > planDetails[currentPlan as keyof typeof planDetails].price ? '+' : ''}
                      ${planDetails[selectedPlan as keyof typeof planDetails].price - planDetails[currentPlan as keyof typeof planDetails].price}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Change (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter reason for plan change..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedPlan === currentPlan}
              className="btn-primary"
            >
              {loading ? 'Changing Plan...' : 'Change Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
